/**
 * @file UserContext.js
 * @author Simon Tenedero
 * @created 2025-06-11
 * @lastModified 2025-07-02
 * @description file containing UserContext, decided to implement a user context to prevent prop drilling
 */

import { createContext, useContext, useState, useEffect } from "react";
import { fetchUserInfo } from "../utils/usersUtils";
import { logWebsiteUsage } from "../utils/usersUtils";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import { useAuth } from "../hooks/useAuth.js"; // Custom hook for authentication

//1. create the context
const UserContext = createContext();

/**
 * 2. Hook to access user context
 *
 * @returns {Object} Object containing user, setUser, loading, loginTime, and setLoginTime
 */
export const useUser = () => {
  const context = useContext(UserContext); //provides the context value provided by the nearest UserProvider
  if (!context) {
    //this ensures useUser is not used with children not nested in a provider - this catches 'undefined'
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

/**
 * 3. create the context provider, 'children' is a special prop that represents any nested JSX inside the component
 *
 * UserProvider component provides user authentication state and user data to the application.
 * It listens for authentication state changes and updates the user context accordingly.
 */
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null); //create a local state inside UserProvider
  const [loading, setLoading] = useState(true); //used when auth state hasn't resolved yet - "checking if user is signed in on app startup"
  const [loginTime, setLoginTime] = useState(null); // Session tracking

  //IF WE USE CLOUD FUNCTIONS TO CREATE USER DOCUMENTS, WE CAN USE THE FOLLOWING CODE:
  const { handleSignOut } = useAuth();
  useEffect(() => {
    const auth = getAuth();
    let userDocUnsubscribe = null; //track the user document listener

    //this will trigger whenever handleSignIn() or handleSignOut() is called in ProfileMenu.js
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      //clean up previous user document listener
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
        userDocUnsubscribe = null;
      }

      //check user is signed in
      if (authUser) {
        try {
          await authUser.getIdToken();

          //try and fetch info from firestore using auth uid
          let existingUserData = await fetchUserInfo(authUser.uid);

          //could not fetch user info on first try, proceed with re-fetch attempts and error handling if fail
          if (!existingUserData) {
            //our cloud function in functions/index.js detects when users login for the first time and handles the doc creation
            console.log(
              "user document not found, waiting for cloud function to automatically create it...",
            );

            let attempts = 0;
            const maxAttempts = 10;

            while (!existingUserData && attempts < maxAttempts) {
              await new Promise((resolve) => setTimeout(resolve, 1000)); //delay 1 second
              existingUserData = await fetchUserInfo(authUser.uid); //try and fetch again
              attempts++;
            }

            //after max 10 attempts, still could not retrieve document
            if (!existingUserData) {
              handleSignOut(); //force users to sign up, this will call the onAuthStateChanged cleanup function.
              return;
            }
          }

          //succeeded in fetching user info
          if (existingUserData) {
            const userInfo = {
              uid: authUser.uid,
              ...existingUserData,
            };
            setUser(userInfo);

            //set up real-time listener for user document changes
            const userDocRef = doc(db, "users", authUser.uid);
            let isInitialLoad = true;

            userDocUnsubscribe = onSnapshot(
              userDocRef,
              (docSnapshot) => {
                if (docSnapshot.exists()) {
                  //skip the initial load to avoid immediate reload
                  if (isInitialLoad) {
                    isInitialLoad = false;
                    return;
                  }

                  //only reload if this is a server update (not from cache or pending writes)
                  if (
                    !docSnapshot.metadata.hasPendingWrites &&
                    !docSnapshot.metadata.fromCache
                  ) {
                    const updatedUserData = docSnapshot.data();

                    //check if role changed (most important change requiring reload)
                    if (
                      updatedUserData.role?.updatedRoleAt !==
                      existingUserData.role?.updatedRoleAt
                    ) {
                      console.log(
                        "User role changed, initiating page reload...",
                      );

                      //notify user of permission change, then force reload
                      alert(
                        "Your account permissions have been updated. The page will now reload.",
                      );
                      //set delay, so users can view message
                      setTimeout(() => {
                        window.location.reload();
                      }, 2000);
                    } else {
                      //for other changes, just update the context without reload
                      console.log("User data updated, refreshing context...");
                      const updatedUserInfo = {
                        uid: authUser.uid,
                        ...updatedUserData,
                      };
                      setUser(updatedUserInfo);
                    }
                  }
                }
              },
              (error) => {
                console.error(
                  "Error listening to user document changes:",
                  error,
                );
              },
            );

            const isFreshSignIn =
              sessionStorage.getItem("freshSignIn") === "true"; //check that this is a fresh sign-in and not a reload.

            //track login time and log usage
            if (isFreshSignIn) {
              logWebsiteUsage(authUser.uid, `User logged in`, "loginHistory");

              sessionStorage.removeItem("freshSignIn"); // Clear the flag
            }

            //restore loginTime using session storage
            const loginTime = sessionStorage.getItem("loginTime");
            if (loginTime) {
              setLoginTime(new Date(parseInt(loginTime)));
            } else {
              //no stored loginTime, so set to current time
              setLoginTime(new Date());
            }
          } else {
            console.log("Page reload detected, not logging sign-in");
          }
        } catch (error) {
          //catch errors in setting up user
          console.error("Error setting up user:", error);
          setUser(null);
        }
      }
      //clean-up, user signs out
      else {
        setUser(null);
        setLoginTime(null);

        //clear local storage when user is not logged in.
        localStorage.removeItem("lastVideoUrl");
        localStorage.removeItem("lastPage");
      }
      setLoading(false); //auth state is now determined.
    });

    //cleanup function
    return () => {
      unsubscribe();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
    };
  }, []); //don't put user or login time in dependency, causes infinite loop.

  return (
    <UserContext.Provider
      value={{ user, setUser, loading, loginTime, setLoginTime }}
    >
      {/*the value prop is what will be passed to children from the context*/}
      {children}
    </UserContext.Provider>
  );
};
